import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { orderWallets } from "../utils/walletOrder";
import { applyWalletBalance, useWalletBalanceSocket } from "./useWalletBalanceSocket";

/**
 * Session-scoped access latch (mirrors `usePaymentsActivity`): a missing `payments:read` role surfaces as a
 * 401/403 from the runtime, and every denial costs a silent `/auth/refresh` + replay inside HttpApiService.
 * After the first denial we stop asking for the rest of the session.
 */
const deniedPrincipals = new Set<string>();

/** One page is plenty — an org has at most a handful of currency wallets. */
const PAGE_LIMIT = 200;

export interface UseRuntimeWalletsResult {
  wallets: RuntimeWallet[];
  isLoading: boolean;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  error: string | null;
  /** Re-reads the whole list. Use it after activating a wallet, not for a single card. */
  refresh: () => Promise<void>;
  /** Re-reads ONE wallet and patches it in place — no list fetch, no page-level loading state. */
  refreshOne: (walletId: string) => Promise<void>;
  /**
   * Puts the list in the order just saved (`PUT /wallets/order`) without reading it again: the named wallets
   * first, the rest after them as they were — what the backend returns from then on.
   */
  applyOrder: (walletIds: string[]) => void;
  /** Drops a wallet that was just deleted, without reloading the grid. */
  dropWallet: (walletId: string) => void;
}

/**
 * ACTIVATED payment wallets from the runtime (`GET /internal/payments/wallets`).
 *
 * - Org members: the server pins the org from the JWT, so we never send `organization_id` (handoff §2). Call
 *   without arguments.
 * - INTERNAL: not scoped to their own org — they must target a specific client org via `organizationId` (the
 *   reader's org selector). Without one, the hook does not fetch.
 */
export const useRuntimeWallets = (organizationId?: string): UseRuntimeWalletsResult => {
  const { user, isInternalUser } = useAuth();
  const principalKey = user?.id ?? "anon";
  const canQuery = isInternalUser ? !!organizationId : !!user;

  const [wallets, setWallets] = useState<RuntimeWallet[]>([]);
  const [isLoading, setIsLoading] = useState(canQuery);
  const [hasAccess, setHasAccess] = useState(() => !deniedPrincipals.has(principalKey));
  const [error, setError] = useState<string | null>(null);
  // The latest issued request wins — a stale slow response must never overwrite a newer one.
  const requestSeq = useRef(0);

  const fetchWallets = useCallback(async () => {
    if (!canQuery || deniedPrincipals.has(principalKey)) {
      setIsLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await PaymentsWalletsService.list(
        isInternalUser ? { organizationId, limit: PAGE_LIMIT } : { limit: PAGE_LIMIT },
      );
      if (seq !== requestSeq.current) return;
      setWallets(res.data.data);
      setHasAccess(true);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      if (HttpApiService.isAccessDeniedError(err)) {
        deniedPrincipals.add(principalKey);
        setHasAccess(false);
      } else {
        setError(HttpApiService.getErrorMessage(err));
        setWallets([]);
      }
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [canQuery, principalKey, isInternalUser, organizationId]);

  /**
   * One card's refresh button must not reload the page. `fetchWallets` re-reads every wallet AND flips the
   * page-level `isLoading`, so pressing refresh on the COP card blanked the whole grid — including the other
   * currencies, which had nothing to do with it.
   *
   * This reads `GET /wallets/:id` and upserts that row. It deliberately does NOT touch `isLoading` or
   * `requestSeq`: the card owns its own spinner, and a list refresh in flight must still win on its own
   * terms. Ordering is settled by `updated_at`, the same tiebreaker the balance socket uses, so a slow single
   * read can never revert a fresher row.
   */
  const refreshOne = useCallback(
    async (walletId: string) => {
      if (!canQuery || deniedPrincipals.has(principalKey)) return;
      try {
        const res = await PaymentsWalletsService.getById(walletId);
        setWallets((current) => upsertWallet(current, res.data));
      } catch (err: unknown) {
        // A single card failing to refresh is not worth latching access or clearing the list — the value
        // on screen stays the last one the server confirmed.
        if (HttpApiService.isAccessDeniedError(err)) {
          deniedPrincipals.add(principalKey);
          setHasAccess(false);
        }
      }
    },
    [canQuery, principalKey],
  );

  useEffect(() => {
    void fetchWallets();
  }, [fetchWallets]);

  // Live balances. The socket only ever carries the caller's own organization (the server scopes it to the
  // org room), but an INTERNAL reader is looking at ANOTHER org's wallets — so it must ignore pushes that
  // do not belong to the org on screen.
  useWalletBalanceSocket((payload) => {
    if (organizationId && payload.organization_id !== organizationId) return;
    setWallets((current) => applyWalletBalance(current, payload));
  }, canQuery && hasAccess);

  const applyOrder = useCallback((walletIds: string[]) => {
    setWallets((current) => orderWallets(current, walletIds));
  }, []);

  const dropWallet = useCallback((walletId: string) => {
    setWallets((current) => current.filter((wallet) => wallet.id !== walletId));
  }, []);

  return {
    wallets,
    isLoading,
    hasAccess,
    error,
    refresh: fetchWallets,
    refreshOne,
    applyOrder,
    dropWallet,
  };
};

/**
 * Replaces one wallet with the row the server just returned, dropping it when a fresher one is already in
 * state (`updated_at`), and returning the SAME array when nothing changed so React skips the re-render.
 *
 * Unlike `applyWalletBalance`, which patches the few fields a socket payload carries, this swaps the whole
 * entity: a manual refresh is also how a card picks up a freeze reason or a status change.
 */
function upsertWallet(wallets: RuntimeWallet[], fresh: RuntimeWallet): RuntimeWallet[] {
  const index = wallets.findIndex((wallet) => wallet.id === fresh.id);
  if (index === -1) return [...wallets, fresh];

  const current = wallets[index];
  if (current.updated_at && fresh.updated_at && fresh.updated_at < current.updated_at) return wallets;

  const next = [...wallets];
  next[index] = fresh;
  return next;
}
